import {
  ResolveRunRequestSchema,
  RunRecoveryResponseSchema,
  type ResolveRunRequest,
  type RunRecoveryResponse,
  type RunStatus,
} from "@herskin/contracts";
import { api } from "./api";

const PROMPT_BLOCKING_RUN_STATES = new Set<RunStatus>([
  "submitting",
  "accepted",
  "running",
  "waiting_approval",
  "waiting_clarification",
  "stopping",
  "recovering",
  "unknown",
]);

/** 与服务端 ACTIVE_RUN_STATES 保持一致，避免明知会 409 仍允许再次提交文字或附件。 */
export function runBlocksNewPrompt(status: RunStatus | null | undefined) {
  return Boolean(status && PROMPT_BLOCKING_RUN_STATES.has(status));
}

export function promptBlockedHint(status: RunStatus | null | undefined) {
  if (status === "unknown") return "请先处理上方的“结果待确认”；HerSkin 不会重复发送文字或附件";
  if (status === "stopping") return "正在等待 Hermes 确认停止，请勿重复发送";
  if (runBlocksNewPrompt(status)) return "当前会话仍有任务在运行，任务结束后即可继续发送";
  return null;
}

/** 只查询并收敛真实状态；此接口绝不会重放 prompt 或附件 RPC。 */
export async function reconcileRun(sessionId: string, runId: string) {
  return RunRecoveryResponseSchema.parse(
    await api<unknown>(
      `/api/sessions/${encodeURIComponent(sessionId)}/runs/${encodeURIComponent(runId)}/reconcile`,
      { method: "POST" },
    ),
  );
}

/** 人工裁决只在远端证据不足后使用，固定 confirm 字面量防止普通请求误调用。 */
export async function resolveRun(
  sessionId: string,
  runId: string,
  outcome: ResolveRunRequest["outcome"],
) {
  const body = ResolveRunRequestSchema.parse({ outcome, confirm: "resolve_unknown_result" });
  return RunRecoveryResponseSchema.parse(
    await api<unknown>(
      `/api/sessions/${encodeURIComponent(sessionId)}/runs/${encodeURIComponent(runId)}/resolve`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  );
}

export function recoveryResultMessage(result: RunRecoveryResponse) {
  if (result.state === "running") return "已确认 Hermes 仍在执行，本条消息无需重发。";
  if (result.state === "completed") {
    return result.reason === "user_confirmed"
      ? "已按确认将本轮标记为完成，可以继续对话。"
      : "已从 Hermes 记录确认本轮完成，可以继续对话。";
  }
  if (result.state === "failed") {
    return result.reason === "prompt_not_attempted"
      ? "已确认消息正文未提交；原附件不会自动重传，可以重新组织后发送。"
      : "本轮已收敛为未执行/失败；原附件不会自动重传。";
  }
  return "Hermes 没有足够证据判断本轮是否执行，请根据实际结果人工确认。";
}
